import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, Phone, MapPin, Hammer, Clock, Award, Users } from "lucide-react";
import ApexLoader from "@/components/ApexLoader";

const STATUS_STYLES = {
  new: { label: "NEW", cls: "bg-[#dc2626]/15 text-[#fca5a5] border-[#dc2626]/40" },
  reviewing: { label: "REVIEWING", cls: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30" },
  approved: { label: "APPROVED", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  rejected: { label: "REJECTED", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30" },
};

export default function DashboardApplications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/contractor-applications");
        setItems(data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="applications-page">
      <div>
        <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">admin · founding network</div>
        <h1 className="font-display text-4xl text-white tracking-tight">
          Contractor <em className="silver-text not-italic">applications</em>.
        </h1>
        <p className="font-body text-sm text-zinc-400 mt-2">
          {items.length} of 100 founding-network applications received.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-3 py-6" data-testid="dash-applications-loading">
          <ApexLoader inline size={18} />
          <span className="font-mono-apex text-xs uppercase tracking-[0.3em] text-zinc-500">loading</span>
        </div>
      )}
      {error && <div className="font-mono-apex text-xs text-[#dc2626]">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => {
          const status = STATUS_STYLES[it.status] || STATUS_STYLES.new;
          return (
            <div key={it.id} className="bg-[#121214] border border-white/5 p-6" data-testid="application-card">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="font-display text-2xl text-white leading-tight">{it.company}</div>
                  <div className="font-body text-sm text-zinc-300 mt-1">{it.name}</div>
                </div>
                <span className={`font-mono-apex text-[9px] uppercase tracking-[0.25em] px-2 py-1 border ${status.cls}`}>
                  {status.label}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-4 font-mono-apex text-[11px] text-zinc-400">
                <div className="flex items-center gap-2 truncate"><Mail size={11} /> {it.email}</div>
                <div className="flex items-center gap-2"><Phone size={11} /> {it.phone}</div>
                <div className="flex items-center gap-2"><MapPin size={11} /> {it.state} · {it.primary_zip}</div>
                <div className="flex items-center gap-2"><Users size={11} /> {it.crews_count} crew{it.crews_count === 1 ? "" : "s"} · {it.years_active}y</div>
              </div>
              {it.services?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {it.services.map((s) => (
                    <span key={s} className="px-2 py-0.5 border border-white/10 font-mono-apex text-[9px] uppercase tracking-[0.15em] text-zinc-300">
                      {s}
                    </span>
                  ))}
                </div>
              )}
              {it.certifications && (
                <div className="flex items-center gap-2 mb-3 font-mono-apex text-[10px] text-zinc-400">
                  <Award size={11} className="text-[#dc2626]" /> {it.certifications}
                </div>
              )}
              {it.message && (
                <div className="border-t border-white/5 pt-3 font-body text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">
                  {it.message}
                </div>
              )}
              <div className="border-t border-white/5 mt-3 pt-3 font-mono-apex text-[9px] uppercase tracking-[0.25em] text-zinc-600 flex items-center gap-2">
                <Clock size={10} /> {new Date(it.created_at).toLocaleString()}
              </div>
            </div>
          );
        })}
        {!loading && items.length === 0 && (
          <div className="font-mono-apex text-xs text-zinc-600">No applications yet. Share the landing page to start collecting.</div>
        )}
      </div>
    </div>
  );
}
